import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '../components/Button';
import Validator from '../services/Validator';
import ApiClient from '../services/ApiClient';
import ModalManager from '../services/ModalManager';
import { useI18n } from '../i18n/LanguageProvider';
import contactInfo from '../data/contactInfo';

const Success = ({ id, t }) => (
  <div>
    <h3 style={{marginTop:0}}>{t('booking.successTitle')}</h3>
    <p>{t('booking.successLine')} <strong>{id}</strong>.</p>
    <p className="muted">{t('booking.successNote')}</p>
  </div>
);

const ErrorModal = ({ t, whatsappUrl }) => (
  <div>
    <h3 style={{marginTop:0}}>{t('booking.errorTitle', t('booking.error'))}</h3>
    <p className="muted" style={{ marginTop: 8 }}>{t('booking.errorMessage')}</p>

    <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
      <a className="btn ghost accent" href={whatsappUrl} target="_blank" rel="noreferrer">
        {t('booking.errorWhatsapp', t('booking.whatsappAlt'))}
      </a>
      <a className="btn ghost" href={`mailto:${contactInfo.email}`}>
        {t('booking.errorEmail', t('contact.emailAction', 'Email us'))}
      </a>
    </div>
  </div>
);

const fmt = (iso, locale) => {
  if (!iso) return '';
  const [y,m,d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m-1, d));
  return new Intl.DateTimeFormat(locale || undefined, { day:'2-digit', month:'2-digit', year:'numeric' })
    .format(date)
    .replaceAll('.', '-').replaceAll('/', '-').replace(/\s/g, '');
};

const Field = ({
  label,
  name,
  type = 'text',
  required = false,
  placeholder,
  as = 'input',
  values,
  errors,
  onChange,
  onBlur,
  formatDate,
  onOpenServicePicker,
  onOpenCalendar,
  pickerLabel
}) => {
  const showError = Boolean(errors[name]);
  const common = {
    id: name,
    name,
    onChange,
    onBlur,
    'aria-invalid': showError,
    'aria-describedby': showError ? `${name}-err` : undefined,
    className: 'card',
    style: { padding: '.8rem', width: '100%', borderColor: showError ? 'crimson' : 'var(--border)' }
  };

  const value = values[name] ?? '';

  return (
    <div className="mt-2">
      <label htmlFor={name} style={{ fontWeight: 600 }}>
        {label} {required && <span aria-hidden="true" style={{ color: 'crimson' }}>*</span>}
      </label>

      {as === 'textarea' ? (
        <textarea
          {...common}
          rows={4}
          placeholder={placeholder}
          value={value}
          style={{ ...common.style, resize: 'vertical' }}
        />
      ) : name === 'service' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
          <input
            {...common}
            type="text"
            placeholder={placeholder}
            value={value}
            readOnly
          />
          <button
            type="button"
            className="btn ghost accent"
            onClick={onOpenServicePicker}
            aria-label="Open service picker"
          >
            {pickerLabel || 'Choose'}
          </button>
        </div>
      ) : name === 'date' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0.7,
                color: 'var(--muted)'
              }}
            >
              <path
                fill="currentColor"
                d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3a1 1 0 1 1 2 0v1zm13 6H4v10h16V8zM6 11h4v4H6v-4z"
              />
            </svg>
            <input
              {...common}
              type="text"
              placeholder={placeholder}
              value={formatDate ? formatDate(value) : value}
              readOnly
              style={{ ...common.style, paddingLeft: '2.2rem' }}
            />
          </div>
          <button
            type="button"
            className="btn ghost accent"
            onClick={onOpenCalendar}
            aria-label="Open calendar"
          >
            {pickerLabel || 'Pick'}
          </button>
        </div>
      ) : (
        <input {...common} type={type} placeholder={placeholder} value={value} />
      )}

      {showError && (
        <div
          id={`${name}-err`}
          className="muted"
          role="alert"
          style={{ color: 'crimson', marginTop: 6 }}
        >
          {errors[name]}
        </div>
      )}
    </div>
  );
};

/* ---------- Calendar (modal content) ---------- */
const Calendar = ({ value, onSelect, locale, monthFormat = 'long' }) => {
  const [d, setD] = useState(value ? new Date(value) : new Date());
  const y = d.getFullYear(), m = d.getMonth();

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const days = useMemo(() => {
    const first = new Date(y, m, 1);
    const start = first.getDay() === 0 ? 6 : first.getDay() - 1; // Mon first
    const total = new Date(y, m+1, 0).getDate();
    return { start, total };
  }, [y, m]);

  const pick = (day) => {
    const dt = new Date(y, m, day);
    onSelect(dt.toISOString().slice(0,10));
  };

  const weekFormatter = useMemo(() => new Intl.DateTimeFormat(locale || undefined, { weekday: 'short' }), [locale]);
  const weekLabels = useMemo(() => {
    const base = new Date(2024, 0, 1); // Monday, 1 Jan 2024
    return Array.from({ length: 7 }).map((_, i) => weekFormatter.format(new Date(base.getFullYear(), base.getMonth(), base.getDate() + i)));
  }, [weekFormatter]);

  return (
    <div>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
        <button className="btn ghost" onClick={()=>setD(new Date(y, m-1, 1))}>‹</button>
        <strong>{d.toLocaleString(locale || undefined, { month: monthFormat, year:'numeric' })}</strong>
        <button className="btn ghost" onClick={()=>setD(new Date(y, m+1, 1))}>›</button>
      </div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6, textAlign:'center', fontSize:12, color:'var(--muted)'}}>
        {weekLabels.map(w=><div key={w}>{w}</div>)}
      </div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6, marginTop:6}}>
        {Array.from({length: days.start}).map((_,i)=><div key={'e'+i} />)}
        {Array.from({length: days.total}).map((_,i)=>{
          const day = i+1;
          const current = new Date(y, m, day);
          current.setHours(0, 0, 0, 0);
          const disabled = current < today;
          return (
            <button
              key={day}
              disabled={disabled}
              onClick={()=>pick(day)}
              className="btn ghost"
              style={{
                padding:'.5rem 0',
                ...(disabled ? {
                  color: 'var(--muted)',
                  background: 'var(--bg-soft)',
                  borderColor: 'var(--border)',
                  cursor: 'not-allowed',
                  opacity: 0.65
                } : {})
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ---------- ServicePicker (modal content) ---------- */
const ServicePicker = ({ value, onSelect, services, t }) => {
  return (
    <div>
      <p className="muted" style={{marginTop:0}}>{t('booking.pickerDescription')}</p>
      <div
        style={{
          display:'grid',
          gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',
          gap:'12px'
        }}
      >
        {services.map(s => {
          const active = (s.title === value) || (s.slug === value);
          return (
            <button
              key={s.slug}
              className="card"
              onClick={()=>onSelect(s.title)}
              style={{
                textAlign:'left',
                padding:'12px',
                borderColor: active ? 'var(--primary-600)' : 'var(--border)'
              }}
            >
              <strong>{s.title}</strong>
              <p className="muted" style={{margin:'.25rem 0 0'}}>{s.short}</p>
            </button>
          );
        })}
      </div>
      <p className="muted" style={{marginTop:'12px'}}>{t('booking.pickerTip')}</p>
    </div>
  );
};

const Booking = () => {
  const { services, t, code } = useI18n();
  const location = useLocation();

  const [values, setValues] = useState({
    name: '', email: '', phone: '', service: '', date: '', address: '', message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);

  const whatsappUrl = useMemo(() => {
    const digits = contactInfo.phone.replace(/\D/g, '');
    return `https://wa.me/${digits}?text=Hi! I want to book cleaning with Sparklean.`;
  }, []);

  // --- Preselect service when coming from service detail ---
  useEffect(() => {
    const stateSlug = location.state?.preselect;
    const qs = new URLSearchParams(location.search);
    const queryVal = qs.get('service');
    const candidate = stateSlug || queryVal || '';
    if (!candidate) return;

    const found =
      services.find(s => s.slug === candidate)?.title ||
      services.find(s => s.title.toLowerCase() === candidate.toLowerCase())?.title ||
      '';

    if (found) {
      setValues(v => ({ ...v, service: found }));
      setTouched(t => ({ ...t, service: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, services]);

  const validator = useMemo(() => new Validator({
    name: [Validator.required(t('booking.required'))],
    email: [Validator.required(t('booking.required')), Validator.email(t('booking.emailError'))],
    phone: [Validator.required(t('booking.required')), Validator.phone(t('booking.phoneError'))],
    service: [Validator.required(t('booking.required'))],
    date: [Validator.required(t('booking.required'))],
    address: [Validator.required(t('booking.required')), Validator.min(5, t('booking.addressHint'))],
    message: []
  }), [t]);

  const validateIfTouched = (vals) => {
    const all = validator.validate(vals);
    const filtered = {};
    Object.keys(all).forEach(k => { if (touched[k]) filtered[k] = all[k]; });
    setErrors(filtered);
  };

  const onChange = e => {
    const { name, value } = e.target;
    const next = { ...values, [name]: value };
    setValues(next);
    validateIfTouched(next);
  };

  const onBlur = e => {
    const { name } = e.target;
    const nextTouched = { ...touched, [name]: true };
    setTouched(nextTouched);
    const all = validator.validate(values);
    const filtered = {};
    Object.keys(all).forEach(k => { if (nextTouched[k]) filtered[k] = all[k]; });
    setErrors(filtered);
  };

  const openCalendar = () => {
    ModalManager.open(Calendar, {
      title: t('booking.calendarTitle'),
      value: values.date,
      locale: code,
      monthFormat: t('booking.calendarMonth', 'long'),
      onSelect: (iso) => { setValues(v => ({...v, date: iso})); setTouched(t => ({...t, date:true})); ModalManager.close(); }
    });
  };

  const openServicePicker = () => {
    ModalManager.open(ServicePicker, {
      title: t('booking.pickerTitle'),
      value: values.service || '',
      services,
      t,
      onSelect: (title) => { setValues(v => ({...v, service: title})); setTouched(t => ({...t, service:true})); ModalManager.close(); }
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const allTouched = Object.keys(values).reduce((a,k)=> (a[k]=true, a), {});
    setTouched(allTouched);
    const errs = validator.validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    const res = await ApiClient.submitBooking(values).catch(()=>({ok:false}));
    setBusy(false);
    if (res.ok) {
      ModalManager.open(Success, { id: res.id, title: t('booking.successTitle'), t });
      setValues({ name:'', email:'', phone:'', service:'', date:'', address:'', message:'' });
      setTouched({});
      setErrors({});
    } else {
      ModalManager.open(ErrorModal, {
        title: t('booking.errorTitle', t('booking.error')),
        t,
        whatsappUrl
      });
    }
  };

  return (
    <section className="section">
      <div className="container">
        <h1>{t('booking.title')}</h1>
        <p className="muted">{t('booking.subtitle')}</p>

        <form onSubmit={onSubmit} className="card route-enter" style={{padding:'1.25rem', marginTop:'1rem'}}>
          <div style={{display:'grid', gap:'1rem', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))'}}>
            <Field label={t('booking.fullName', t('booking.nameField'))} name="name" required placeholder={t('booking.fullNamePlaceholder')}
              values={values} errors={errors} onChange={onChange} onBlur={onBlur}
            />
            <Field label={t('booking.emailField')} name="email" required placeholder={t('booking.emailField')}
              type="email"
              values={values} errors={errors} onChange={onChange} onBlur={onBlur}
            />
            <Field label={t('booking.phoneField')} name="phone" required placeholder={t('booking.phonePlaceholder')}
              values={values} errors={errors} onChange={onChange} onBlur={onBlur}
            />
            <Field label={t('booking.serviceField')} name="service" required
              values={values} errors={errors} onChange={onChange} onBlur={onBlur}
              onOpenServicePicker={openServicePicker}
              placeholder={t('booking.chooseService')}
              pickerLabel={t('booking.pick')}
            />
            <Field label={t('booking.dateField')} name="date" required
              values={values} errors={errors} onChange={onChange} onBlur={onBlur}
              formatDate={(v)=>fmt(v, code)}
              placeholder={t('booking.datePlaceholder')}
              onOpenCalendar={openCalendar}
              pickerLabel={t('booking.pick')}
            />
            <Field label={t('booking.addressField')} name="address" required placeholder={t('booking.addressPlaceholder')}
              values={values} errors={errors} onChange={onChange} onBlur={onBlur}
            />
          </div>

          <Field label={`${t('booking.notesField')} (${t('optional', 'optional')})`} name="message" as="textarea"
            placeholder={t('booking.notesPlaceholder')}
            values={values} errors={errors} onChange={onChange} onBlur={onBlur}
          />

          <div className="mt-3" style={{display:'flex', gap:12, flexWrap:'wrap'}}>
            <Button variant="primary" disabled={busy}>{busy ? `${t('booking.submit')}…` : t('booking.submit')}</Button>
            <a className="btn ghost" href={whatsappUrl} target="_blank" rel="noreferrer">{t('booking.whatsappAlt')}</a>
          </div>
        </form>

        <p className="muted mt-2">{t('booking.privacy')}</p>
      </div>
    </section>
  );
};
export default Booking;
